import React, { useState, useEffect } from 'react';
import {
  Chip,
  Tooltip,
  CircularProgress,
  Box,
  IconButton,
  Typography,
  Alert
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';
import appointmentsApi from '../api/appointments';
import { useQuery } from '@tanstack/react-query';

const SyncStatusIndicator = ({ 
  appointmentId, 
  size = 'small',
  showDetails = false,
  autoRefresh = true,
  refreshInterval = 5000 
}) => {
  const [manualRefresh, setManualRefresh] = useState(0);

  // Fetch sync status
  const { data: syncStatus, isLoading, error, refetch } = useQuery({
    queryKey: ['appointmentSyncStatus', appointmentId, manualRefresh],
    queryFn: () => appointmentsApi.getAppointmentSyncStatus(appointmentId),
    enabled: !!appointmentId,
    refetchInterval: (data) => {
      return autoRefresh && data?.sync_status === 'pending' ? refreshInterval : false;
    },
    staleTime: 2000,
  });

  const handleRefresh = () => {
    setManualRefresh(prev => prev + 1);
    refetch();
  };

  const getSyncStatusDisplay = () => {
    if (isLoading) {
      return {
        icon: <CircularProgress size={16} />,
        label: 'Loading...',
        color: 'default',
        severity: 'info'
      };
    }

    if (error) {
      return {
        icon: <ErrorIcon fontSize="small" />,
        label: 'Error',
        color: 'error',
        severity: 'error'
      };
    }

    if (!syncStatus) {
      return {
        icon: <PendingIcon fontSize="small" />,
        label: 'Unknown',
        color: 'default',
        severity: 'warning'
      };
    }

    switch (syncStatus.sync_status) {
      case 'synced':
        return {
          icon: <CheckCircleIcon fontSize="small" />,
          label: 'Synced',
          color: 'success',
          severity: 'success',
          tooltip: `Synced with Pabau ${syncStatus.pabau_id ? `(ID: ${syncStatus.pabau_id})` : ''}`
        };
      
      case 'pending':
        return {
          icon: <SyncIcon fontSize="small" className="rotating" />,
          label: 'Syncing...',
          color: 'warning',
          severity: 'warning',
          tooltip: `Pending sync to Pabau${syncStatus.outbox?.attempts ? ` (Attempts: ${syncStatus.outbox.attempts})` : ''}`
        };
      
      case 'failed':
        return {
          icon: <ErrorIcon fontSize="small" />,
          label: 'Sync Failed',
          color: 'error',
          severity: 'error',
          tooltip: syncStatus.outbox?.last_error || 'Failed to sync with Pabau'
        };
      
      case 'local_only':
        return {
          icon: <CheckCircleIcon fontSize="small" />,
          label: 'Local Only',
          color: 'info',
          severity: 'info',
          tooltip: 'This appointment is stored locally only'
        };
      
      default:
        return {
          icon: <PendingIcon fontSize="small" />,
          label: syncStatus.sync_status,
          color: 'default',
          severity: 'info'
        };
    }
  };

  const status = getSyncStatusDisplay();

  if (size === 'small') {
    return (
      <Tooltip title={status.tooltip || status.label}>
        <Chip
          icon={status.icon}
          label={status.label}
          size="small"
          color={status.color}
          variant="outlined"
          sx={{
            '& .rotating': {
              animation: 'spin 2s linear infinite',
            },
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      </Tooltip>
    );
  }

  if (showDetails && syncStatus) {
    return (
      <Box>
        <Alert 
          severity={status.severity} 
          icon={status.icon}
          action={
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        >
          <Typography variant="body2" component="div">
            <strong>Sync Status:</strong> {status.label}
          </Typography>
          
          {syncStatus.pabau_id && (
            <Typography variant="caption" display="block">
              Pabau ID: {syncStatus.pabau_id}
            </Typography>
          )}
          
          {syncStatus.sync_mode && (
            <Typography variant="caption" display="block">
              Mode: {syncStatus.sync_mode}
            </Typography>
          )}
          
          {syncStatus.version && (
            <Typography variant="caption" display="block">
              Version: {syncStatus.version}
            </Typography>
          )}
          
          {syncStatus.last_updated && (
            <Typography variant="caption" display="block">
              Last Updated: {new Date(syncStatus.last_updated).toLocaleString()}
            </Typography>
          )}
          
          {syncStatus.outbox && (
            <Box mt={1}>
              <Typography variant="caption" display="block">
                <strong>Outbox Status:</strong> {syncStatus.outbox.status}
              </Typography>
              {syncStatus.outbox.attempts > 0 && (
                <Typography variant="caption" display="block">
                  Attempts: {syncStatus.outbox.attempts}
                </Typography>
              )}
              {syncStatus.outbox.last_error && (
                <Typography variant="caption" display="block" color="error">
                  Error: {syncStatus.outbox.last_error}
                </Typography>
              )}
              {syncStatus.outbox.last_attempt && (
                <Typography variant="caption" display="block">
                  Last Attempt: {new Date(syncStatus.outbox.last_attempt).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip title={status.tooltip || status.label}>
        <Box display="flex" alignItems="center" gap={0.5}>
          {status.icon}
          <Typography variant="body2" color={`${status.color}.main`}>
            {status.label}
          </Typography>
        </Box>
      </Tooltip>
      <IconButton size="small" onClick={handleRefresh}>
        <RefreshIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default SyncStatusIndicator;